import merge from './merge';
import getType from './getType';

export const defaultOptions = {
  debug: 0,
  preload: false,
  autoplay: false,
  playbackRate: 1.0,
  volume: 1.0,
  muted: false,
  qualityLevel: 'medium',
  repeat: 'none',
  language: 'en',
  source: null,
  sourceOptions: null,

  nowPlaying: {
    interval: 20000,
    maxHistory: 20,
    fetchArtwork: true
  },

  analytics: {
    label: null,
    category: null,
    url: null
  },

  googleAnalytics: {
    enabled: false
  },

  ads: {
    enabled: false,
    requestTimeout: 5000,
    skipOffset: 10,
    rerollTimeout: 480,
    pofToken: ''
  },

  adswizz: {
    metadataUrl: null,
    afrUrl: null,
    zoneAlias: null
  },

  gdpr: false,

  ui: {
    layout: 'legacy',
    language: 'en',
    contentType: 'podcast',
    sharing: 0,
    subscribe: 0,
    artwork: true,
    playlist: true,
    download: 0,
    description: false,
    branding: true,
    websiteUrl: null,
    waveform: true,
    dateTimeFormat: 'j M ga',
    theme: {
      rounded: 0,
      text: '444444',
      background: 'ffffff',
      accent: 'ee3b96',
      border: null
    }
  },

  hlsjs: {}
};

export const optionsSchema = {
  type: 'object',
  properties: {
    debug: {
      type: 'number',
      default: 0,
      max: 2
    },
    preload: {
      type: 'boolean',
      default: false
    },
    autoplay: {
      type: 'boolean',
      default: false
    },
    playbackRate: {
      type: 'number',
      default: 1,
      min: 0.4,
      max: 3
    },
    volume: {
      type: 'number',
      default: 1,
      min: 0,
      max: 1
    },
    muted: {
      type: 'boolean',
      default: false
    },
    qualityLevel: {
      type: 'string',
      default: 'medium'
    },
    repeat: {
      type: 'string',
      default: 'none',
      enum: ['none', 'single', 'playlist']
    },
    language: {
      type: 'string',
      default: 'en'
    },

    source: {
      oneOf: [
        {
          type: 'null'
        },
        {
          type: 'string',
          format: 'uri'
        },
        {
          type: 'array',
          items: {
            oneOf: [
              {
                type: 'string',
                format: 'uri'
              },
              {
                type: 'object',
                properties: {}
              }
            ]
          }
        },
        {
          type: 'object',
          properties: {}
        }
      ]
    },

    gdpr: {
      oneOf: [
        {
          type: 'boolean',
          default: false
        },
        {
          type: 'object',
          required: ['userConsent'],
          properties: {
            userConsent: {
              type: 'string'
            }
          }
        }
      ]
    },

    nowPlaying: {
      type: 'object',
      properties: {
        interval: {
          type: 'number',
          default: 20000
        },
        maxHistory: {
          type: 'number',
          default: 20
        },
        fetchArtwork: {
          type: 'boolean',
          default: defaultOptions.nowPlaying.fetchArtwork
        }
      }
    },

    analytics: {
      type: 'object',
      properties: {
        label: {
          type: ['string', 'null']
        },
        category: {
          type: ['string', 'null']
        },
        url: {
          type: ['string', 'null'],
          format: 'uri'
        }
      }
    },

    googleAnalytics: {
      type: 'object',
      properties: {
        enabled: {
          type: 'boolean',
          default: defaultOptions.googleAnalytics.enabled
        }
      }
    },

    ads: {
      type: 'object',
      properties: {
        enabled: {
          type: 'boolean',
          default: false
        },
        requestTimeout: {
          type: 'number',
          default: 5000
        },
        skipOffset: {
          type: 'number',
          default: 10
        },
        rerollTimeout: {
          type: 'number',
          default: 480,
          min: 120
        },
        pofToken: {
          type: 'string',
          default: defaultOptions.ads.pofToken
        }
      }
    },

    adswizz: {
      type: 'object',
      properties: {
        metadataUrl: {
          type: 'string',
          default: defaultOptions.adswizz.metadataUrl
        },
        afrUrl: {
          type: 'string',
          default: defaultOptions.adswizz.afrUrl
        },
        zoneAlias: {
          oneOf: [
            {
              type: 'string',
              default: defaultOptions.adswizz.zoneAlias
            },
            {
              type: 'array',
              minItems: 1,
              items: {
                type: 'string',
                default: defaultOptions.adswizz.zoneAlias
              }
            }
          ]
        }
      }
    },

    ui: {
      type: 'object',
      additionalFields: false,
      properties: {
        layout: {
          type: 'string',
          default: 'legacy'
        },
        language: {
          type: 'string',
          default: 'en'
        },
        contentType: {
          type: 'string',
          default: 'podcast',
          enum: ['podcast', 'stream']
        },
        sharing: {
          enum: [0, 1, 2, true, false],
          default: defaultOptions.ui.sharing
        },
        subscribe: {
          enum: [0, 1, 2],
          default: defaultOptions.ui.subscribe
        },
        artwork: {
          type: 'boolean',
          default: true
        },
        branding: {
          oneOf: [
            {
              type: 'boolean',
              default: defaultOptions.ui.branding
            },
            {
              type: 'string'
            }
          ]
        },
        websiteUrl: {
          type: 'string',
          format: 'uri',
          default: defaultOptions.ui.websiteUrl
        },
        playlist: {
          type: 'boolean',
          default: true
        },
        download: {
          type: 'boolean',
          enum: [0, 1, 2, true, false],
          default: defaultOptions.ui.download
        },
        description: {
          type: 'boolean',
          default: false
        },
        waveform: {
          type: 'boolean',
          default: true
        },
        dateTimeFormat: {
          type: 'string',
          default: defaultOptions.ui.dateTimeFormat
        },
        theme: {
          type: 'object',
          additionalFields: false,
          properties: {
            text: {
              type: 'string',
              format: 'color-hex',
              default: defaultOptions.ui.theme.text
            },
            background: {
              type: 'string',
              format: 'color-hex',
              default: defaultOptions.ui.theme.background
            },
            accent: {
              type: 'string',
              format: 'color-hex',
              default: defaultOptions.ui.theme.accent
            },
            border: {
              type: ['string', 'null'],
              format: 'color-hex',
              default: defaultOptions.ui.theme.border
            },
            rounded: {
              type: 'number',
              default: defaultOptions.ui.theme.rounded,
              min: 0,
              max: 40
            }
          }
        }
      }
    }
  }
};

/**
 * Validate an object against a schema
 *
 * @param  {Object} input
 * @param  {Object} schema
 * @param  {String} path
 * @param  {Array}  errors
 * @return {Boolean}
 */
function validateObject (input, schema, path, errors = []) {
  // validate required fields
  let requiredValid = true;
  if (Array.isArray(schema.required)) {
    for (let i = 0; i < schema.required.length; i++) {
      if (typeof input[schema.required[i]] === 'undefined') {
        errors.push(`Invalid value for '${path}', missing required field '${schema.required[i]}'`);
        requiredValid = false;
        break;
      }
    }
  }

  let propertiesValid = true;
  Object.keys(input).forEach(function (key) {
    const _path = path ? path + '.' + key : key;

    if (typeof schema.properties[key] === 'undefined') {
      if (schema.additionalFields === false) {
        if (console && console.warn) console.warn(`Unrecognized option '${_path}'`);
      }
    } else if (!validate(input[key], schema.properties[key], _path, errors)) {
      propertiesValid = false;
    }
  });

  return requiredValid && propertiesValid;
}

/**
 * Validate an inputs type against a schema
 *
 * @param  {mixed} input
 * @param  {Object} schema
 * @param  {String} path
 * @param  {Array}  errors
 * @return {Boolean}
 */
function validateType (input, schema, path, errors = []) {
  if (input === null || typeof schema.type === 'undefined') return true;
  if (Array.isArray(schema.type)) {
    if (schema.type.indexOf(getType(input)) === -1) {
      const values = schema.type.slice(0, -1).join(', ') + ' or ' + schema.type.slice(-1);
      errors.push(`Invalid type for '${path}', expected one of ${values} but got '${getType(input)}'`);
      return false;
    }
    return true;
  }
  if (getType(input) !== schema.type) {
    errors.push(`Invalid type for '${path}', expected a ${schema.type} but got '${getType(input)}'`);
    return false;
  }
  return true;
}

/**
 * Validate a string format against a schema
 *
 * @param  {String} input
 * @param  {Object} schema
 * @param  {String} path
 * @param  {Array}  errors
 * @return {Boolean}
 */
function validateFormat (input, schema, path, errors = []) {
  switch (schema.format) {
    case 'color-hex':
      if (input.match(/^#?([0-9a-fA-F]{3}){1,2}$/) === null) {
        errors.push(`Invalid format for '${path}', expected a valid hexidecimal colour but got '${input}'`);
        return false;
      }
      break;

    // case 'uri':
    //   if (input.match(/^([a-z0-9]+:\/\/)?.+$/i) === null) {
    //     errors.push(`Invalid format for '${path}', expected a valid uri but got '${input}'`);
    //     return false;
    //   }
    //   break;
  }
  return true;
}

/**
 * Validate a string against a schema
 *
 * @param  {String} input
 * @param  {Object} schema
 * @param  {String} path
 * @param  {Array}  errors
 * @return {Boolean}
 */
function validateString (input, schema, path, errors = []) {
  if (schema.format && input) {
    return validateFormat(input, schema, path, errors);
  }

  return true;
}

/**
 * Validate a number against a schema
 *
 * @param  {Number} input
 * @param  {Object} schema
 * @param  {String} path
 * @param  {Array}  errors
 * @return {Boolean}
 */
function validateNumber (input, schema, path, errors = []) {
  if (typeof schema.min !== 'undefined' && input < schema.min) {
    errors.push(`Invalid value for '${path}', expected a number greater than ${schema.min} but got '${input}'`);
    return false;
  }

  if (typeof schema.max !== 'undefined' && input > schema.max) {
    errors.push(`Invalid value for '${path}', expected a number less than ${schema.max} but got '${input}'`);
    return false;
  }

  return true;
}

/**
 * Validate an array against a schema
 *
 * @param  {Array} input
 * @param  {Object} schema
 * @param  {String} path
 * @param  {Array}  errors
 * @return {Boolean}
 */
function validateArray (input, schema, path, errors = []) {
  if (schema.minItems && input.length < schema.minItems) {
    errors.push(`Invalid value for '${path}', expected an array with at least ${schema.minItems} but got '${input.length}'`);
    return false;
  }

  let childrenValid = true;
  for (let i = 0; i < input.length; i++) {
    if (!validate(input[0], schema.items, path + '[0]', errors)) {
      childrenValid = false;
    }
  }

  return childrenValid;
}

/**
 * Validate that the input matches one of the schemas
 *
 * @param  {mixed} input
 * @param  {Object} schema
 * @param  {String} path
 * @param  {Array}  errors
 * @return {Boolean}
 */
function validateOneOf (input, schema, path, errors = []) {
  for (let i = 0; i < schema.oneOf.length; i++) {
    if (validateType(input, schema.oneOf[i], path)) {
      return validate(input, schema.oneOf[i], path, errors);
    }
  }

  let types = schema.oneOf.reduce(function (types, subschema) {
    types.push(subschema.type);
    return types;
  }, []);

  types = types.slice(0, -1).join(', ') + ' or ' + types.slice(-1);

  errors.push(`Invalid type for '${path}', expected one of ${types} but got '${getType(input)}'`);

  return false;
}

/**
 * Validate the input agains a schema
 *
 * @param  {Object} options
 * @param  {Object} schema
 * @param  {String} path
 * @param  {Array}  errors
 * @return {Boolean}
 */
function validate (options, schema, path = '', errors = []) {
  if (Array.isArray(schema.enum) && schema.enum.indexOf(options) === -1) {
    const values = schema.enum.slice(0, -1).join(', ') + ' or ' + schema.enum.slice(-1);
    errors.push(`Invalid value for '${path}', expected one of ${values} but got '${options.toString()}'`);
    return false;
  }

  if (Array.isArray(schema.oneOf)) {
    return validateOneOf(options, schema, path, errors);
  }

  if (validateType(options, schema, path, errors)) {
    switch (getType(options)) {
      case 'object':
        return validateObject(options, schema, path, errors);
      case 'string':
        return validateString(options, schema, path, errors);
      case 'number':
        return validateNumber(options, schema, path, errors);
      case 'array':
        return validateArray(options, schema, path, errors);
    }
  }

  return true;
}

/**
 * Validate options against a schema
 *
 * @param  {Object} options
 * @return {Object}
 */
export function validateOptions (options) {
  const errors = [];
  const valid = validate(options, optionsSchema, '', errors);
  return {
    valid,
    errors
  };
}

/**
 * Merge one or more options objects together
 *
 * @return {Object}
 */
export function mergeOptions () {
  return merge(defaultOptions, ...arguments);
}

export default {
  defaultOptions,
  optionsSchema,
  validateOptions,
  mergeOptions
};
